/**
 * Map Markers Renderer
 * Renders interactive markers on the map with tooltips
 */

import type { StolpersteineData, Stolperstein } from '../../types';

export function renderMapMarkers(data: StolpersteineData | null): void {
  const mapWrapper = document.querySelector<HTMLElement>('.map-section__image-wrapper');
  if (!mapWrapper || !data?.stolpersteine) return;

  // Reference image dimensions
  const refWidth = 1634;
  const refHeight = 1322;

  // Create markers container
  const markersContainer = document.createElement('div');
  markersContainer.className = 'map-markers-container';

  // Track active marker for mobile
  let activeMarker: HTMLElement | null = null;

  data.stolpersteine.forEach((stone: Stolperstein) => {
    if (!stone.koordinaten) return;

    // Calculate percentage position
    const xPercent = (stone.koordinaten.x / refWidth) * 100;
    const yPercent = (stone.koordinaten.y / refHeight) * 100;

    // Create marker element
    const marker = document.createElement('div');
    marker.className = 'map-marker';
    marker.style.cssText = `left: ${xPercent}%; top: ${yPercent}%; pointer-events: auto;`;
    marker.dataset.id = String(stone.id);

    // Adjust tooltip position if marker is near edges
    if (yPercent < 18) {
      marker.classList.add('map-marker--tooltip-bottom');
    }
    if (xPercent > 80) {
      marker.classList.add('map-marker--tooltip-left');
    } else if (xPercent < 20) {
      marker.classList.add('map-marker--tooltip-right');
    }

    // Create number badge
    const numberBadge = document.createElement('span');
    numberBadge.className = 'map-marker__number';
    numberBadge.textContent = String(stone.nummer);

    // Create tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'map-marker__tooltip';
    tooltip.innerHTML = `
      <div class="map-marker__tooltip-name">${stone.name}</div>
      <div class="map-marker__tooltip-address">${stone.adresse}</div>
    `;

    marker.appendChild(numberBadge);
    marker.appendChild(tooltip);

    // Event handlers
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isTouchDevice) {
      // Mobile: First tap shows tooltip, second tap or tooltip tap navigates
      marker.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (activeMarker === marker && marker.classList.contains('is-active')) {
          // Second tap - navigate to page
          window.location.href = `stolperstein/${stone.slug}.html`;
        } else {
          // First tap - show tooltip
          if (activeMarker) {
            activeMarker.classList.remove('is-active');
          }
          marker.classList.add('is-active');
          activeMarker = marker;
        }
      });

      // Tap on tooltip navigates
      tooltip.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
        window.location.href = `stolperstein/${stone.slug}.html`;
      });
    } else {
      // Desktop: Click navigates directly (hover shows tooltip via CSS)
      marker.addEventListener('click', () => {
        window.location.href = `stolperstein/${stone.slug}.html`;
      });
    }

    // Keyboard accessibility
    marker.setAttribute('tabindex', '0');
    marker.setAttribute('role', 'button');
    marker.setAttribute('aria-label', `${stone.name}, ${stone.adresse}`);

    marker.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        window.location.href = `stolperstein/${stone.slug}.html`;
      }
    });

    markersContainer.appendChild(marker);
  });

  // Close active marker when clicking outside
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (activeMarker && !target.closest('.map-marker')) {
      activeMarker.classList.remove('is-active');
      activeMarker = null;
    }
  });

  mapWrapper.appendChild(markersContainer);
}
